import { Haptics, ImpactStyle } from '@capacitor/haptics'
import {
	type ActionFunctionArgs,
	type MetaFunction,
	type LoaderFunctionArgs,
	json,
} from '@remix-run/node'
import {
	Link,
	redirect,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { withZod } from '@remix-validated-form/with-zod'
import { ArrowRightIcon, CheckCircle2Icon } from 'lucide-react'
import { validationError } from 'remix-validated-form'
import { z } from 'zod'
import { Button } from '#app/components/ui/button'
import { usePlatform } from '#app/hooks/useIsNative'
import { useOptionalUser } from '#app/hooks/useUser'
import { sendOTP } from '#app/services/twilio'
import { getUserId } from '#app/utils/auth.server'
import { prisma } from '#app/utils/db.server'

export const meta: MetaFunction = () => [{ title: "Let's Rally!" }]

export async function loader({ request }: LoaderFunctionArgs) {
	const userId = await getUserId(request)
	if (userId) return redirect('/app')
	return json({
		appStoreUrl: process.env.APP_STORE_URL,
		signupEnabled: process.env.FEATURE_FLAG_SIGNUP_ENABLED === 'true',
	})
}

const validator = withZod(
	z.object({
		phone: z.string().min(10, 'Invalid phone number.'),
		isNative: z.string().optional(),
	}),
)

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const { error, data } = await validator.validate(formData)
	if (error) return validationError(error)

	if (
		process.env.FEATURE_FLAG_SIGNUP_ENABLED === 'true' ||
		data.isNative === 'true'
	) {
		// TODO: Remove after app store testing
		if (data.phone === '5555555555') {
			return redirect(`/verify?phone=${data.phone}`)
		}

		await sendOTP(data.phone)
		return redirect(`/verify?phone=${data.phone}`)
	} else {
		await prisma.user.upsert({
			where: { phone: data.phone },
			create: { phone: data.phone, isOnWaitlist: true },
			update: { isOnWaitlist: true },
		})
		return redirect(`/?waitlist=true`)
	}
}

export default function Index() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const { isNative } = usePlatform()
	const [searchParams] = useSearchParams()
	const waitlist = !!searchParams.get('waitlist')

	return (
		<div className="flex h-full flex-col justify-center">
			<div className="mt-16 flex grow flex-col items-center gap-4 px-8 md:flex-row">
				<img
					src="/img/lets-rally.png"
					alt="lets rally"
					className="mt-4 size-[250px] sm:mt-0 md:size-[400px]"
				/>
				<div className="mt-8 flex flex-col gap-8 sm:mt-0">
					<p className="text-center font-handwriting text-[30px] md:text-left">
						The Ultimate App for a spontaneous summer.
					</p>
					{user ? (
						<Button size="lg" asChild>
							<Link to="/app">Dashboard</Link>
						</Button>
					) : waitlist ? (
						<div className="flex items-center justify-center gap-4 rounded-full border border-green-400 bg-green-50 p-3 text-green-700">
							<p className="font-handwriting text-3xl">Waitlist joined!</p>
							<div>
								<CheckCircle2Icon size={32} className="text-green-600" />
							</div>
						</div>
					) : (
						<Button
							size="lg"
							className={isNative ? 'mx-auto' : 'w-fit font-bold'}
							onClick={() => Haptics.impact({ style: ImpactStyle.Light })}
							asChild
						>
							<Link to="/signin">
								{data.signupEnabled || isNative
									? 'Get started'
									: 'Join waitlist'}
								<ArrowRightIcon size={24} className="ml-2" strokeWidth={3} />
							</Link>
						</Button>
					)}
					{/* {isNative || !data.appStoreUrl || !data.signupEnabled ? null : (
						<a
							href={data.appStoreUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							<img
								src="/img/app-store-badge.svg"
								alt="Download on the App Store"
								className="h-12"
							/>
						</a>
					)} */}
				</div>
			</div>
			{/* {isNative ? null : (
				<div
					className={cn(
						'mx-auto flex flex-wrap items-center justify-center gap-2 py-4 text-sm text-muted-foreground/75',
						isNative && 'mb-4',
					)}
				>
					<p className="text-center">
						Brock Software LLC © {new Date().getFullYear()}
					</p>
					<span>&#183;</span>
					<p>
						<Link to="/privacy-policy" className="hover:underline">
							Privacy Policy
						</Link>
					</p>
					<span>&#183;</span>
					<p>
						Contact us at{' '}
						<span className="font-bold">letsrally@brock.software</span>
					</p>
				</div>
			)} */}
		</div>
	)
}
